import _extends from "@babel/runtime/helpers/extends";
import _typeof from "@babel/runtime/helpers/typeof";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import React, { memo, useEffect, useMemo, useState } from 'react';
import MuiAutocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { debounce } from '@material-ui/core/utils';
import Input from '../Input';
import MenuItem from '../MenuItem';
import Chip from '../Chip';
import { Avatar } from '../index';
var filter = createFilterOptions();

var getChipAvatar = function (option) {
  var icon = option.icon,
      avatar = option.avatar,
      image = option.image;
  if (icon) return icon;
  if (avatar) return /*#__PURE__*/React.createElement(Avatar, {
    src: avatar,
    size: 'small'
  });
  if (image) return /*#__PURE__*/React.createElement(Avatar, {
    src: image,
    size: 'small',
    rounded: true
  });
  return undefined;
};

var AutoComplete = function (props) {
  var hide = props.hide,
      inputProps = props.inputProps,
      multiple = props.multiple,
      label = props.label,
      placeholder = props.placeholder,
      loading = props.loading,
      error = props.error,
      helperText = props.helperText,
      variant = props.variant,
      chipColor = props.chipColor,
      onChange = props.onChange,
      _props$options = props.options,
      options = _props$options === void 0 ? [] : _props$options,
      value = props.value,
      defaultValue = props.defaultValue,
      getData = props.getData,
      allowCreate = props.allowCreate,
      allowDuplicates = props.allowDuplicates,
      _props$showCreateText = props.showCreateText,
      showCreateText = _props$showCreateText === void 0 ? true : _props$showCreateText,
      setCreateText = props.setCreateText,
      chipProps = props.chipProps,
      createOnBlur = props.createOnBlur,
      rest = _objectWithoutPropertiesLoose(props, ["hide", "inputProps", "multiple", "label", "placeholder", "loading", "error", "helperText", "variant", "chipColor", "onChange", "options", "value", "defaultValue", "getData", "allowCreate", "allowDuplicates", "showCreateText", "setCreateText", "chipProps", "createOnBlur"]);

  var _useState = useState(false),
      open = _useState[0],
      setOpen = _useState[1];

  var _useState2 = useState(''),
      inputValue = _useState2[0],
      setInputValue = _useState2[1];

  var getDataDebounce = typeof getData === 'function' ? debounce(getData, 500) : undefined;
  useEffect(function () {
    if (open && getDataDebounce) getDataDebounce(inputValue);
  }, [open]);
  useEffect(function () {
    if (open && getDataDebounce && inputValue) getDataDebounce(inputValue);
  }, [inputValue]);

  var handleChange = function (_event, newValue) {
    var _ref, _newValue$inputValue;

    if (!newValue) return;
    var ids = !multiple ? (_ref = (_newValue$inputValue = newValue == null ? void 0 : newValue.inputValue) != null ? _newValue$inputValue : newValue == null ? void 0 : newValue.id) != null ? _ref : newValue : newValue.map(function (item) {
      var _ref2, _item$inputValue;

      return (_ref2 = (_item$inputValue = item == null ? void 0 : item.inputValue) != null ? _item$inputValue : item == null ? void 0 : item.id) != null ? _ref2 : item;
    });

    if (multiple && !allowDuplicates) {
      var setIds = new Set(ids);
      ids = Array.from(setIds);
    }

    onChange == null ? void 0 : onChange(ids, newValue);
  };

  var getValue = function (valueIds) {
    if (!Array.isArray(valueIds)) {
      if (_typeof(valueIds) === 'object') return valueIds;
      return options.find(function (item) {
        return item.id === valueIds;
      }) || valueIds;
    }

    return valueIds.map(function (id) {
      if (_typeof(id) === 'object') return id;
      return options.find(function (item) {
        return item.id === id;
      }) || id;
    });
  };

  var objValue = useMemo(function () {
    return getValue(value);
  }, [value]);
  var defaultObjValue = useMemo(function () {
    return getValue(defaultValue);
  }, [defaultValue]);
  if (hide) return null;
  return /*#__PURE__*/React.createElement(MuiAutocomplete, _extends({
    onOpen: function onOpen() {
      return setOpen(true);
    },
    onClose: function onClose() {
      return setOpen(false);
    },
    value: objValue,
    defaultValue: defaultObjValue,
    options: options,
    loading: loading,
    multiple: multiple,
    filterSelectedOptions: true,
    disableCloseOnSelect: multiple,
    noOptionsText: "Not found",
    loadingText: "Loading...",
    getOptionSelected: function getOptionSelected(option, value) {
      if (option != null && option.id) return (option == null ? void 0 : option.id) === (value == null ? void 0 : value.id);
      return option === value;
    },
    getOptionLabel: function getOptionLabel(option) {
      // Value selected with enter, right from the input
      if (typeof option === 'string') return option; // Add "xxx" option created dynamically

      if (option.inputValue) return option.inputValue; // Regular option

      return option.name;
    },
    onInputChange: function handleInputChange(_e, newInputValue) {
      var _newInputValue$trim;

      // @ts-ignore
      setInputValue((_newInputValue$trim = newInputValue == null ? void 0 : newInputValue.trim()) != null ? _newInputValue$trim : '');
    },
    onChange: handleChange,
    renderOption: function renderOption(item) {
      var id = item.id,
          data = item.data,
          inputValue = item.inputValue,
          rest = _objectWithoutPropertiesLoose(item, ["id", "data", "inputValue"]);

      return /*#__PURE__*/React.createElement(MenuItem, _extends({
        key: id,
        value: inputValue || id
      }, rest, {
        justContent: true
      }));
    }
  }, rest, {
    freeSolo: allowCreate,
    filterOptions: function filterOptions(options, params) {
      var _params$inputValue;

      var filtered = filter(options, params);
      var inputValue = params == null ? void 0 : (_params$inputValue = params.inputValue) == null ? void 0 : _params$inputValue.trim();

      if (!!inputValue.length && allowCreate && showCreateText) {
        filtered.push({
          id: new Date().getTime().toString(),
          inputValue: inputValue,
          name: (setCreateText == null ? void 0 : setCreateText(inputValue)) || "Add \"" + inputValue + "\""
        });
      }

      return filtered;
    },
    renderInput: function renderInput(params) {
      return /*#__PURE__*/React.createElement(Input, _extends({}, params, {
        loading: loading,
        placeholder: placeholder,
        label: label,
        helperText: helperText,
        error: error,
        variant: variant,
        onBlur: function onBlur(e) {
          if (!!inputValue && createOnBlur && allowCreate && Array.isArray(objValue)) {
            handleChange(e, [].concat(objValue, [inputValue]));
          }
        }
      }, inputProps));
    },
    renderTags: function renderTags(tagValue, getTagProps) {
      return tagValue.map(function (option, index) {
        var name = option.name,
            id = option.id,
            color = option.color;
        return /*#__PURE__*/React.createElement(Chip, _extends({}, getTagProps({
          index: index
        }), {
          color: color || chipColor,
          key: id || index + "-" + option,
          rounded: false,
          outlined: true,
          avatar: getChipAvatar(option),
          label: name || option
        }, chipProps));
      });
    }
  }));
};

export default /*#__PURE__*/memo(AutoComplete);